

import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { useToast } from "@/context/ToastContext";
import { MAIN_URL } from "@/lib/api";
import { 
  Database, 
  RefreshCw, 
  Trash2, 
  Download, 
  Upload,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Users,
  Calendar,
  Package,
  CreditCard,
  FileText,
  TrendingUp
} from "lucide-react";

interface DataSummary {
  master_data: {
    event_types: number;
    payment_methods: number;
    vendor_types: number;
    expense_categories: number;
  };
  core_entities: {
    venues: number;
    vendors: number;
    services: number;
    packages: number;
  };
  operational_data: {
    clients: number;
    enquiries: number;
    events: number;
    expenses: number;
    invoices: number;
    tasks: number;
  };
  users: {
    total_users: number;
  };
}

interface DatabaseStats {
  table_counts: Record<string, number>;
  total_records: number;
}

// API functions
const dataManagementApi = {
  getSummary: async () => {
    const response = await fetch(`${MAIN_URL}/api/data/summary`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Accept': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch data summary');
    return response.json();
  },
  getStats: async () => {
    const response = await fetch(`${MAIN_URL}/api/data/stats`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Accept': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch database stats');
    return response.json();
  },
  resetData: async (preserveUsers: boolean = true) => {
    const response = await fetch(`${MAIN_URL}/api/data/reset`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ confirm: true, preserve_users: preserveUsers }),
    });
    if (!response.ok) throw new Error('Failed to reset data');
    return response.json();
  },
  generateDummyData: async () => {
    const response = await fetch(`${MAIN_URL}/api/data/generate-dummy`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to generate dummy data');
    return response.json();
  },
};

// Stat Card Component
function StatCard({ 
  title, 
  value, 
  icon: Icon, 
  color 
}: { 
  title: string; 
  value: number; 
  icon: React.ElementType; 
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-stone-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-stone-800">{value.toLocaleString()}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

// Action Card Component
function ActionCard({
  title,
  description,
  icon: Icon,
  buttonText,
  buttonColor,
  onClick,
  danger = false,
}: {
  title: string;
  description: string;
  icon: React.ElementType;
  buttonText: string;
  buttonColor: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${buttonColor}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-stone-800 mb-1">{title}</h3>
          <p className="text-sm text-stone-500 mb-4">{description}</p>
          <button
            onClick={onClick}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              danger
                ? "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                : "bg-stone-100 text-stone-700 hover:bg-stone-200"
            }`}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DataManagementPage() {
  const { showToast } = useToast();
  const [summary, setSummary] = useState<DataSummary | null>(null);
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [preserveUsers, setPreserveUsers] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryRes, statsRes] = await Promise.all([
        dataManagementApi.getSummary(),
        dataManagementApi.getStats(),
      ]);
      setSummary(summaryRes.data);
      setStats(statsRes.data);
    } catch (error) {
      showToast("Failed to load data summary", "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetData = async () => {
    try {
      setActionLoading("reset");
      const result = await dataManagementApi.resetData(preserveUsers);
      showToast(result.message, "success");
      setShowResetConfirm(false);
      loadData();
    } catch (error) {
      showToast("Failed to reset data", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateDummyData = async () => {
    try {
      setActionLoading("dummy");
      const result = await dataManagementApi.generateDummyData();
      showToast(result.message, "success");
      loadData();
    } catch (error) {
      showToast("Failed to generate dummy data", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportData = () => {
    // Export functionality would be implemented here
    showToast("Export functionality coming soon!", "info");
  };

  const handleImportData = () => {
    // Import functionality would be implemented here
    showToast("Import functionality coming soon!", "info");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Data Management</h1>
          <p className="text-stone-500">Manage your database, export/import data, and generate test data</p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-xl text-stone-600 hover:bg-stone-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Overview Stats */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Clients"
            value={summary.operational_data.clients}
            icon={Users}
            color="bg-blue-500"
          />
          <StatCard
            title="Total Events"
            value={summary.operational_data.events}
            icon={Calendar}
            color="bg-green-500"
          />
          <StatCard
            title="Total Invoices"
            value={summary.operational_data.invoices}
            icon={FileText}
            color="bg-purple-500"
          />
          <StatCard
            title="Database Records"
            value={stats?.total_records || 0}
            icon={Database}
            color="bg-amber-500"
          />
        </div>
      )}

      {/* Detailed Stats */}
      {summary && (
        <div className="bg-white rounded-xl border border-stone-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            Data Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Master Data */}
            <div>
              <h3 className="text-sm font-medium text-stone-500 mb-3">Master Data</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Event Types</span>
                  <span className="font-medium">{summary.master_data.event_types}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Payment Methods</span>
                  <span className="font-medium">{summary.master_data.payment_methods}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Vendor Types</span>
                  <span className="font-medium">{summary.master_data.vendor_types}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Expense Categories</span>
                  <span className="font-medium">{summary.master_data.expense_categories}</span>
                </div>
              </div>
            </div>

            {/* Core Entities */}
            <div>
              <h3 className="text-sm font-medium text-stone-500 mb-3">Core Entities</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Venues</span>
                  <span className="font-medium">{summary.core_entities.venues}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Vendors</span>
                  <span className="font-medium">{summary.core_entities.vendors}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Services</span>
                  <span className="font-medium">{summary.core_entities.services}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Packages</span>
                  <span className="font-medium">{summary.core_entities.packages}</span>
                </div>
              </div>
            </div>

            {/* Operational Data */}
            <div>
              <h3 className="text-sm font-medium text-stone-500 mb-3">Operational Data</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Enquiries</span>
                  <span className="font-medium">{summary.operational_data.enquiries}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Expenses</span>
                  <span className="font-medium">{summary.operational_data.expenses}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Tasks</span>
                  <span className="font-medium">{summary.operational_data.tasks}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Users</span>
                  <span className="font-medium">{summary.users.total_users}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <ActionCard
          title="Generate Dummy Data"
          description="Create sample clients, events, and enquiries for testing purposes."
          icon={TrendingUp}
          buttonText={actionLoading === "dummy" ? "Generating..." : "Generate Data"}
          buttonColor="bg-green-500"
          onClick={handleGenerateDummyData}
        />
        <ActionCard
          title="Export Data"
          description="Export your data to JSON or CSV format for backup or migration."
          icon={Download}
          buttonText="Export"
          buttonColor="bg-blue-500"
          onClick={handleExportData}
        />
        <ActionCard
          title="Import Data"
          description="Import data from JSON or CSV files to restore or migrate data."
          icon={Upload}
          buttonText="Import"
          buttonColor="bg-purple-500"
          onClick={handleImportData}
        />
        <ActionCard
          title="Reset All Data"
          description="Clear all data and restore master data to default. This action cannot be undone."
          icon={Trash2}
          buttonText={actionLoading === "reset" ? "Resetting..." : "Reset Data"}
          buttonColor="bg-red-500"
          onClick={() => setShowResetConfirm(true)}
          danger
        />
      </div>

      {/* Table Stats */}
      {stats && (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200">
            <h2 className="text-lg font-semibold text-stone-800">Database Tables</h2>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-stone-50 sticky top-0">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-stone-500 uppercase">Table Name</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-stone-500 uppercase">Records</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {Object.entries(stats.table_counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([table, count]) => (
                    <tr key={table} className="hover:bg-stone-50">
                      <td className="px-6 py-3 text-sm text-stone-700 font-mono">{table}</td>
                      <td className="px-6 py-3 text-sm text-stone-700 text-right">{count.toLocaleString()}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-stone-800">Reset All Data?</h3>
                <p className="text-sm text-stone-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-stone-600 mb-4">
              This will delete all clients, events, enquiries, and transactions. Master data (event types, payment methods, etc.) will be restored to defaults.
            </p>
            <label className="flex items-center gap-2 mb-6">
              <input
                type="checkbox"
                checked={preserveUsers}
                onChange={(e) => setPreserveUsers(e.target.checked)}
                className="rounded border-stone-300 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-sm text-stone-600">Preserve user accounts</span>
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-4 py-2 border border-stone-200 rounded-xl text-stone-600 hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetData}
                disabled={actionLoading === "reset"}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {actionLoading === "reset" ? "Resetting..." : "Yes, Reset All"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
